import React, { useEffect } from 'react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Center, Flex, Heading, Image, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createCustomWifiIcon } from 'clipsal-cortex-icons/src/custom-dynamic-icons';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { useViewportType } from 'clipsal-cortex-utils/src/hooks/use-viewport-type';

import { useReduxDispatch } from '../../../app/store';
import chargerOfflineImg from '../../../assets/images/evse_charger_station_offline.svg';
import chargerOnlineImg from '../../../assets/images/evse_charger_station_online.svg';
import chargerOfflineRingImg from '../../../assets/images/evse_charger_status_offline.svg';
import chargerOnlineRingImg from '../../../assets/images/evse_charger_status_online.svg';
import { ArcButton } from '../../../common/components/ArcButton';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT } from '../../../common/constants';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { trackWifiConfigOpened } from '../../../utils/common/analytics';
import { trackDynatraceWifiConfigOpened } from '../../../utils/native/dynatrace-events';
import { selectFirmwareUpdateStatus } from '../../smart-amp/evseStreamSlice';
import { isEvChargerOffline } from '../ev-charger-helper';
import {
  deactivateEvCharger,
  fetchEvChargerDetails,
  fetchEvChargerWifiDetails,
  selectEvChargerData,
  selectEvseType,
} from './evChargerSlice';

/* istanbul ignore next -- @preserve */
export const DeviceDetails = () => {
  const {
    siteDeviceId,
    charger_id: chargerId,
    isDeactivating,
    hasFetchedEvChargerDetails,
    firmware,
    model,
    sn,
    status,
    wifiSsid,
  } = useSelector(selectEvChargerData);
  const evseType = useSelector(selectEvseType);
  const { cloudFirmwareStatus } = useSelector(selectFirmwareUpdateStatus);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { isDesktopViewport } = useViewportType();
  const backURL = useParamBackUrl('../../account/dashboard?direction=back');

  // The charger is online in every status except the Unavailable_* family — it still
  // reports Charging/Suspended/Faulted etc. over its cloud connection
  const isOffline = isEvChargerOffline(status);
  const chargerStatusImg = isOffline ? chargerOfflineRingImg : chargerOnlineRingImg;
  const chargerImg = isOffline ? chargerOfflineImg : chargerOnlineImg;
  const WifiIcon = createCustomWifiIcon(undefined, '');
  const connectionStatusText = isOffline
    ? t('EVSE.charger not connected')
    : t('Set Up Hardware.charger connected', { ssid: wifiSsid || t('EVSE.wifi') });

  // Always refetch on mount (not gated on hasFetchedEvChargerDetails) so a firmware
  // update triggered outside our SSE flow (e.g. an external portal) is still reflected
  // the next time this screen is visited, instead of only relying on the SSE success event.
  useEffect(() => {
    if (siteDeviceId) {
      dispatch(fetchEvChargerDetails(siteDeviceId));
    }
  }, [siteDeviceId, dispatch]);

  useEffect(() => {
    if (chargerId) {
      dispatch(fetchEvChargerWifiDetails(chargerId));
    }
  }, [chargerId, dispatch]);

  useEffect(() => {
    if (cloudFirmwareStatus === 'Success' && siteDeviceId) {
      dispatch(fetchEvChargerDetails(siteDeviceId));
    }
  }, [cloudFirmwareStatus, siteDeviceId, dispatch]);

  function handleRemoveDevice() {
    if (status === 'Charging') {
      toast({
        title: t('EVSE.cannot remove charger'),
        description: t('EVSE.cannot remove charger description'),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    onOpen();
  }

  return (
    <SlidingAnimationPageBase
      title={t('Hardware.hardware')}
      backURL={backURL}
      customNavigationDirection="forward"
      px={4}
    >
      {!hasFetchedEvChargerDetails ? (
        <CenteredLoader text={`${t('Common.loading')}...`} />
      ) : (
        <>
          <Box h={MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT} overflow={'auto'} pb={10} data-testid="device-details-screen">
            <Center pt={4} pb={2}>
              <Box position="relative" w="48vw" h="48vw">
                <Image w="100%" h="100%" src={chargerStatusImg} alt="EV Charger Setup Successful" />
                <Image
                  src={chargerImg}
                  alt="EV Charger"
                  position={'absolute'}
                  h="62%"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                />
              </Box>
            </Center>
            <Heading textAlign="center" mt={2} fontSize={24} fontWeight={600}>
              {t('Set Up Hardware.charger details')}
            </Heading>
            <Text mt={2} textAlign="center" fontSize={16} fontWeight={400}>
              {connectionStatusText}
            </Text>

            <Flex flexDirection="column">
              <Flex mb={1} align="Center" justify="space-between">
                <Box>{t('Hardware.connection')}</Box>
              </Flex>
              <SEHomeCard borderTopLeftRadius={5} borderBottomRightRadius={'25px'} textAlign={'center'}>
                <Flex
                  w="100%"
                  py={2}
                  as="button"
                  onClick={() => {
                    trackWifiConfigOpened();
                    trackDynatraceWifiConfigOpened();
                    navigate('../device-wifi-configuration');
                  }}
                  data-testid="wifi-config-button"
                >
                  <WifiIcon boxSize={5} mr={3} />
                  <Text>{wifiSsid || t('EVSE.wifi')}</Text>
                  <ChevronRightIcon color={'customGrey.400'} ml="auto" w={6} h={6} />
                </Flex>
              </SEHomeCard>
            </Flex>

            <Flex direction={'column'} py={4} mx={isDesktopViewport ? 4 : 0} borderRadius={isDesktopViewport ? 4 : 0}>
              <Flex direction={'column'} data-testid="device-model-name">
                <Text fontWeight={'semibold'}>{t('Common.model name')}</Text>
                <Text>{t('Set Up Hardware.ev charger')}</Text>
              </Flex>
              <Flex direction={'column'} mt={4} data-testid="device-id">
                <Text fontWeight={'semibold'}>{t('Set Up Hardware.charger id')}</Text>
                <Text>{chargerId}</Text>
              </Flex>
              {model && (
                <Flex direction={'column'} mt={4} data-testid="device-model">
                  <Text fontWeight={'semibold'}>{t('Common.model number')}</Text>
                  <Text>{`${model} - ${evseType}`}</Text>
                </Flex>
              )}
              <Flex direction={'column'} mt={4} data-testid="device-sn">
                <Text fontWeight={'semibold'}>{t('Common.serial number')}</Text>
                <Text>{sn}</Text>
              </Flex>
              <Flex direction={'column'} mt={4} data-testid="device-manufacturer">
                <Text fontWeight={'semibold'}>{t('Hardware.manufacturer')}</Text>
                <Text>{t('Set Up Hardware.schneider electric')}</Text>
              </Flex>
              <Flex direction={'column'} mt={4} data-testid="device-firmware">
                <Text fontWeight={'semibold'}>{t('Common.firmware')}</Text>
                <Text>{firmware}</Text>
              </Flex>
            </Flex>
          </Box>

          <ArcButton
            arcColor="#3DCD58"
            data-testid="device-remove-button"
            w="100%"
            py={6}
            onClick={handleRemoveDevice}
            isLoading={isDeactivating}
            isFixedAtScreenBottom={true}
            isDisabled={status === 'Charging'}
          >
            {t('Set Up Hardware.remove device')}
          </ArcButton>

          <AlertDialogModal
            {...{ isOpen, onClose }}
            header={t('Set Up Hardware.remove charger?')}
            subHeader={t('Set Up Hardware.remove charger confirmation')}
            confirmButtonName={t('Common.remove')}
            onConfirm={async () => {
              try {
                unwrapResult(await dispatch(deactivateEvCharger(siteDeviceId)));
                navigate('../device-removed');
              } catch (error) {
                toast({
                  title: t('Set Up Hardware.error deactivating charger'),
                  description: t('Common.please contact support'),
                  status: 'error',
                  isClosable: true,
                });
              }
            }}
            dialogContentTestId="device-remove-dialog"
            cancelButtonTestId="device-dialog-cancel-button"
            confirmButtonTestId="device-dialog-remove-button"
          />
        </>
      )}
    </SlidingAnimationPageBase>
  );
};
